import { useState, type ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { LanguageBar } from '@/design-system/LanguageSwitcher'
import { celebrate } from '@/design-system/effects'
import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { LegalLink } from '@/features/event/components/LegalReader'
import { readBooleanSetting } from '@/features/event/types'

import { PhotoField } from '../PhotoField'
import { useRubrics } from '@/features/event/useRubrics'
import { PublicLayout } from '@/layouts/PublicLayout'
import { ApiError } from '@/lib/apiClient'

import { useApply, useCompetitionCategories } from '../api'
import { EducationSuggestions } from '../components'
import {
  ACTIVITY_MAX,
  AGE_MAX,
  AGE_MIN,
  EDUCATION_MAX,
  MOTTO_MAX,
  WORD_MAX,
  serverProfileFields,
  validateProfile,
  wordsPayload,
  type ProfileField,
} from '../profile'

/**
 * Candidature Miss & Master.
 *
 * Un formulaire qui demande une photo, un âge et un numéro de téléphone doit
 * dire pourquoi il les demande. D'où les trois consentements séparés, jamais
 * pré-cochés, chacun avec sa propre phrase.
 */
export function ApplyPage() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()

  const eventQuery = usePublicEvent()
  const event = eventQuery.data
  // La même barre de rubriques que partout ailleurs : on ne se retrouve pas
  // enfermé dans la candidature sans moyen de revenir à la carte.
  const { navItems } = useRubrics()
  const categoriesQuery = useCompetitionCategories(event?.slug)
  const apply = useApply(event?.slug)

  const categories = categoriesQuery.data ?? []
  const open = categories.filter((category) => category.applications_open)

  const [categoryId, setCategoryId] = useState(searchParams.get('categorie') ?? '')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [stageName, setStageName] = useState('')
  const [age, setAge] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [bio, setBio] = useState('')
  const [instagram, setInstagram] = useState('')
  // Le profil public : ce que la salle lira avant de voter.
  const [activity, setActivity] = useState('')
  const [educationLevel, setEducationLevel] = useState('')
  const [motto, setMotto] = useState('')
  const [words, setWords] = useState<[string, string, string]>(['', '', ''])
  // Les erreurs ne s'affichent qu'après une première tentative d'envoi :
  // rougir un champ qu'on n'a pas encore eu le temps de remplir n'aide pas.
  const [attempted, setAttempted] = useState(false)
  const [photo, setPhoto] = useState<{ media_id: string; url: string } | null>(null)
  const [rules, setRules] = useState(false)
  const [imageRights, setImageRights] = useState(false)
  const [privacy, setPrivacy] = useState(false)

  // Réglage de l'édition : seuls les détenteurs d'un billet payé candidatent.
  const ticketRequired = readBooleanSetting(
    event?.settings ?? {},
    'competition.applications_require_ticket',
  )
  // Le refus serveur fait foi même si le réglage n'était pas encore chargé :
  // c'est lui qui a vérifié le billet, pas cette page.
  const ticketMissing =
    apply.error instanceof ApiError && apply.error.code === 'APPLICATION_TICKET_REQUIRED'

  const selected = categories.find((category) => category.id === categoryId) ?? open[0] ?? null
  const activeId = categoryId || selected?.id || ''

  const profileErrors = validateProfile(
    { age, activity, educationLevel, motto, words },
    { required: true },
  )
  // Les bornes d'âge de la catégorie choisie, dites avant l'envoi plutôt
  // qu'apprises par un refus.
  const minAge = selected?.eligibility.min_age
  const maxAge = selected?.eligibility.max_age
  if (!profileErrors.age && age) {
    if (minAge && Number(age) < minAge) {
      profileErrors.age = { key: 'competition.ageFrom', params: { min: minAge } }
    } else if (maxAge && Number(age) > maxAge) {
      profileErrors.age = { key: 'competition.ageUntil', params: { max: maxAge } }
    }
  }
  // Un refus du serveur sur un champ que la page n'a pas vu venir : on le
  // signale sous le champ, dans la langue de la personne.
  const serverFields =
    apply.error instanceof ApiError && apply.error.status === 422
      ? serverProfileFields(apply.error.details)
      : []
  const errorFor = (field: ProfileField): string | undefined => {
    const local = attempted ? profileErrors[field] : undefined
    if (local) return t(local.key, local.params ?? {})
    if (serverFields.includes(field)) return t('candidateProfile.errors.invalid')
    return undefined
  }
  const hasProfileErrors = Object.keys(profileErrors).length > 0

  if (eventQuery.isPending || categoriesQuery.isPending) {
    return (
      <Container className="py-32">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-64 w-full max-w-2xl" />
      </Container>
    )
  }

  if (!event) {
    return (
      <>
        <LanguageBar />
        <Container className="py-32">
          <h1 className="font-display text-display-lg text-content-primary">
            {t('state.noEventTitle')}
          </h1>
        </Container>
      </>
    )
  }

  if (apply.isSuccess) {
    return (
      <PublicLayout
      eventName={event.name}
      logoUrl={event.logo_url}
      availableLocales={event.locales}
      navItems={navItems}
    >
        <Container className="py-32">
          <Overline>{t('competition.overline')}</Overline>
          <h1 className="mt-3 font-display text-display-md text-content-primary">
            {t('apply.thanksTitle')}
          </h1>
          {/* Le message vient du serveur : il dit exactement ce qui va se passer. */}
          <p className="mt-5 max-w-xl text-body-lg text-content-secondary">
            {apply.data.message}
          </p>
          <p className="mt-3 max-w-xl text-content-muted">{t('apply.thanksHelp')}</p>
          <Link to="/" className="mt-10 inline-block text-champagne-300 underline">
            {t('order.backHome')}
          </Link>
        </Container>
      </PublicLayout>
    )
  }

  const submit = (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault()
    setAttempted(true)
    if (!activeId || hasProfileErrors) return

    apply.mutate({
      competition_category_id: activeId,
      first_name: firstName,
      last_name: lastName,
      stage_name: stageName || null,
      age: Number(age),
      email,
      phone: phone || null,
      // La bio est saisie dans la langue courante ; l'équipe traduira si besoin.
      bio_i18n: bio ? { [i18n.language]: bio } : {},
      socials: instagram ? { instagram } : {},
      activity: activity.trim(),
      education_level: educationLevel.trim(),
      motto: motto.trim(),
      three_words: wordsPayload(words),
      photo_media_id: photo?.media_id ?? null,
      locale: i18n.language,
      consents: { rules, image_rights: imageRights, privacy },
    }, {
      // La candidature est déposée : la page de remerciement s'ouvre sur une gerbe.
      onSuccess: () => celebrate(),
    })
  }

  return (
    <PublicLayout
      eventName={event.name}
      logoUrl={event.logo_url}
      availableLocales={event.locales}
      navItems={navItems}
    >
      <Container className="max-w-2xl py-24">
        <Overline>{t('competition.overline')}</Overline>
        <h1 className="mt-3 font-display text-display-md text-content-primary">
          {t('apply.title')}
        </h1>

        {open.length > 0 && ticketRequired ? (
          // Dit avant le formulaire, pas après l'envoi : découvrir la règle
          // une fois la photo choisie et les trois cases cochées, c'est
          // perdre son élan — et souvent sa candidature.
          <div className="mt-8 rounded-lg border border-champagne-400/40 bg-champagne-400/5 px-5 py-4 text-sm text-content-secondary">
            <p>{t('apply.ticketRequiredNotice')}</p>
            <Link to="/billets" className="mt-2 inline-block text-champagne-300 underline">
              {t('apply.ticketRequiredCta')}
            </Link>
          </div>
        ) : null}

        {open.length === 0 ? (
          <p className="mt-8 text-body-lg text-content-secondary">{t('apply.allClosed')}</p>
        ) : (
          <form className="mt-10 space-y-8" onSubmit={submit}>
            <fieldset className="space-y-3">
              <legend className="overline text-content-muted">{t('apply.category')}</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {open.map((category) => (
                  <label
                    key={category.id}
                    className={`cursor-pointer rounded-lg border px-4 py-3 ${
                      activeId === category.id
                        ? 'border-champagne-400 text-content-primary'
                        : 'border-line text-content-secondary'
                    }`}
                  >
                    <input
                      type="radio"
                      name="category"
                      className="sr-only"
                      checked={activeId === category.id}
                      onChange={() => setCategoryId(category.id)}
                    />
                    <span className="font-display text-heading-sm">{category.name}</span>
                    {category.seats_remaining !== null ? (
                      <span className="mt-1 block text-caption text-content-muted">
                        {t('competition.seatsLeft', { count: category.seats_remaining })}
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
              {selected?.rules ? (
                <Card className="mt-2 whitespace-pre-line text-sm text-content-secondary">
                  {selected.rules}
                </Card>
              ) : null}
            </fieldset>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('ticketing.firstName')} value={firstName} onChange={setFirstName} required />
              <Field label={t('ticketing.lastName')} value={lastName} onChange={setLastName} required />
              <Field label={t('apply.stageName')} value={stageName} onChange={setStageName} />
              <Field
                label={t('apply.age')}
                value={age}
                onChange={setAge}
                type="number"
                required
                min={minAge ?? AGE_MIN}
                max={maxAge ?? AGE_MAX}
                error={errorFor('age')}
              />
              <Field label={t('checkin.email')} value={email} onChange={setEmail} type="email" required />
              <Field label={t('apply.phone')} value={phone} onChange={setPhone} />
            </div>
            {ticketMissing ? (
              // Sous le champ e-mail, parce que c'est souvent lui le
              // coupable : le billet a été acheté avec une autre adresse.
              <div role="alert" className="-mt-4 text-sm text-state-danger">
                <p>{t('apply.ticketRequiredError')}</p>
                <Link to="/billets" className="mt-1 inline-block underline">
                  {t('apply.ticketRequiredCta')}
                </Link>
              </div>
            ) : null}

            {/* Le profil public. Dit d'emblée qu'il sera public : on ne
                choisit pas son adage de la même façon pour l'équipe et pour
                trois cents inconnus. */}
            <fieldset className="space-y-4">
              <legend className="overline text-content-muted">
                {t('candidateProfile.section')}
              </legend>
              <p className="text-sm text-content-secondary">{t('candidateProfile.sectionHint')}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={t('candidateProfile.activity')}
                  value={activity}
                  onChange={setActivity}
                  required
                  maxLength={ACTIVITY_MAX}
                  placeholder={t('candidateProfile.activityPlaceholder')}
                  error={errorFor('activity')}
                />
                <Field
                  label={t('candidateProfile.educationLevel')}
                  value={educationLevel}
                  onChange={setEducationLevel}
                  required
                  maxLength={EDUCATION_MAX}
                  list="education-suggestions"
                  hint={t('candidateProfile.educationHint')}
                  error={errorFor('education_level')}
                />
              </div>
              <EducationSuggestions id="education-suggestions" />
              <Field
                label={t('candidateProfile.motto')}
                value={motto}
                onChange={setMotto}
                required
                maxLength={MOTTO_MAX}
                placeholder={t('candidateProfile.mottoPlaceholder')}
                error={errorFor('motto')}
              />
              <div>
                <p className="text-caption text-content-muted">
                  {t('candidateProfile.threeWords')} *
                </p>
                <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {words.map((word, index) => (
                    <input
                      key={index}
                      value={word}
                      required
                      maxLength={WORD_MAX}
                      aria-label={t('candidateProfile.word', { n: index + 1 })}
                      placeholder={t('candidateProfile.word', { n: index + 1 })}
                      aria-invalid={errorFor('three_words') ? true : undefined}
                      onChange={(changeEvent) => {
                        const next: [string, string, string] = [...words]
                        next[index] = changeEvent.target.value
                        setWords(next)
                      }}
                      className="w-full min-w-0 rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
                    />
                  ))}
                </div>
                <p
                  className={`mt-1 text-xs ${
                    errorFor('three_words') ? 'text-state-danger' : 'text-content-muted'
                  }`}
                >
                  {errorFor('three_words') ?? t('candidateProfile.threeWordsHint')}
                </p>
              </div>
            </fieldset>

            <label className="block text-caption text-content-muted">
              {t('apply.bio')}
              <textarea
                value={bio}
                onChange={(changeEvent) => setBio(changeEvent.target.value)}
                rows={4}
                className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
              />
            </label>

            <Field label={t('apply.instagram')} value={instagram} onChange={setInstagram} />

            {/* La photo se voit sur le site après validation, et sur le
                bulletin de vote le soir J : c'est elle qu'on regarde avant de
                cliquer, bien avant de lire un nom. */}
            <PhotoField slug={event.slug} value={photo} onChange={setPhoto} />

            <fieldset className="space-y-3">
              <legend className="overline text-content-muted">{t('apply.consents')}</legend>
              {/* Trois cases distinctes : accepter le règlement n'est pas céder
                  son droit à l'image. */}
              <Consent
                checked={rules}
                onChange={setRules}
                label={
                  <Trans
                    i18nKey="apply.consentRules"
                    components={{
                      rules: (
                        <LegalLink blockKey="legal.contest" label={t('legal.contest')} />
                      ),
                    }}
                  />
                }
              />
              <Consent
                checked={imageRights}
                onChange={setImageRights}
                label={t('apply.consentImage')}
              />
              <Consent
                checked={privacy}
                onChange={setPrivacy}
                label={
                  <Trans
                    i18nKey="apply.consentPrivacy"
                    components={{
                      privacy: (
                        <LegalLink blockKey="legal.privacy" label={t('legal.privacy')} />
                      ),
                    }}
                  />
                }
              />
            </fieldset>

            {attempted && hasProfileErrors ? (
              <p role="alert" className="text-sm text-state-danger">
                {t('candidateProfile.errors.fixBelow')}
              </p>
            ) : apply.isError && !ticketMissing ? (
              <p className="text-sm text-state-danger">
                {/* Un refus de validation se lit sous chaque champ, traduit ;
                    le message brut du serveur ne sert qu'aux autres cas. */}
                {serverFields.length > 0
                  ? t('candidateProfile.errors.fixBelow')
                  : apply.error instanceof ApiError
                    ? apply.error.message
                    : t('state.errorTitle')}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-4">
              <Button type="submit" size="lg" disabled={apply.isPending || !activeId}>
                {t('apply.submit')}
              </Button>
              <Badge>{t('apply.moderated')}</Badge>
            </div>
          </form>
        )}
      </Container>
    </PublicLayout>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  min,
  max,
  maxLength,
  placeholder,
  list,
  hint,
  error,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
  min?: number | undefined
  max?: number | undefined
  maxLength?: number | undefined
  placeholder?: string | undefined
  /** L'identifiant d'une `<datalist>` de suggestions. */
  list?: string | undefined
  hint?: string | undefined
  /** Déjà traduite. Remplace l'aide tant qu'elle s'applique. */
  error?: string | undefined
}) {
  return (
    <label className="block text-caption text-content-muted">
      {label}
      {required ? ' *' : ''}
      <input
        type={type}
        value={value}
        required={required}
        min={min}
        max={max}
        maxLength={maxLength}
        placeholder={placeholder}
        list={list}
        aria-invalid={error ? true : undefined}
        onChange={(changeEvent) => onChange(changeEvent.target.value)}
        className={`mt-1 w-full min-w-0 rounded-md border bg-bg-base px-3 py-2 text-sm text-content-primary ${
          error ? 'border-state-danger' : 'border-line'
        }`}
      />
      {error ? (
        <span className="mt-1 block text-xs text-state-danger">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-content-muted">{hint}</span>
      ) : null}
    </label>
  )
}

function Consent({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  // Un ReactNode et non une chaîne : le libellé porte le lien qui ouvre le
  // document. Une case dont on ne peut pas lire le texte ne recueille rien.
  label: ReactNode
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-content-secondary">
      <input
        type="checkbox"
        checked={checked}
        onChange={(changeEvent) => onChange(changeEvent.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
      />
      <span>{label}</span>
    </label>
  )
}
